import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@compiled/react';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import AkButton from '@atlaskit/button/custom-theme-button';
import { token } from '@atlaskit/tokens';

import { MutationInterop as Mutation } from '@confluence/apollo-relay-interop/entry-points/MutationInterop.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { Link } from '@confluence/route-manager/entry-points/Link.ts';
import { setSuperAdminClaim } from '@confluence/super-admin-claim';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { InProductHelpButton } from '@confluence/in-product-help/entry-points/InProductHelpButton.ts';

import { AdminKeyMessageCard } from './AdminKeyMessageCard.tsx';
import { EnableSuperAdminMutation } from './EnableSuperAdminMutation.graphql.ts';
import { refreshAKTimeout } from './super-admin-expiration/timeoutUtils.ts';

export const LEARN_MORE_ARTICLE_ID = '1ePHWZLYCbUCb5HG1OqZf2';

const i18n = defineMessages({
	enableSuperAdminErrorTitle: {
		id: 'super-admin.enable.super.admin.error.title',
		defaultMessage: 'Something went wrong',
		description: 'Title for the error if enabling Super Admin fails',
	},

	enableSuperAdminErrorDescription: {
		id: 'super-admin.enable.super.admin.error.description',
		defaultMessage: 'Please refresh and try again.',
		description: 'Description for the error if enabling Super Admin fails',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EntryPointActions = styled.div({
	marginTop: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Separator = styled.span({
	width: '40px',
	textAlign: 'center',
	margin: `0 ${token('space.100')}`,
});

export class SuperAdminEntryPointComponent extends Component {
	static propTypes = {
		intl: PropTypes.object.isRequired,
		createAnalyticsEvent: PropTypes.func.isRequired,
		contentId: PropTypes.string,
		type: PropTypes.oneOf(['page', 'space']),
	};

	/**
	 * Executed after the mutation is complete.
	 * It is used to set the Super Admin claim, and refresh the page to update elevated restrictions.
	 * @param {object} flags
	 * @param {object} session
	 * @return {function}
	 */
	handleMutationComplete = (flags) => (result) => {
		const superAdminSuccessfullyEnabled =
			result?.enableSuperAdmin?.user?.confluence?.roles?.isSuperAdmin;

		if (superAdminSuccessfullyEnabled) {
			setSuperAdminClaim();
			refreshAKTimeout();

			this.props
				.createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: 'superAdminEntryPoint',
						action: 'started',
						actionSubject: 'adminKeySession',
					},
				})
				.fire();

			const reloadCallback = () => window.location.reload();
			// refresh the page on both resolve and reject of the promise as error handling is tricky here.
			// Considered the options:
			// 1. Showing a flag with error message and reloading
			//   - Flags don't seem preserve after reload
			// 2. Showing a flag with error message and not reloading
			//   - AK session is already acquired so its not a great user experience if we don't reflect it in the UI.
			//     And currently refreshing the page is the way for Admin key banner to show up.
			this.storeConfirmedPage();
			reloadCallback();
		} else {
			this.handleMutationError(flags)();
		}
	};

	/**
	 * Error handling mechanism for network or server errors.
	 * @param {object} flags
	 * @return {function}
	 */
	handleMutationError = (flags) => () => {
		const { intl, createAnalyticsEvent } = this.props;

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'superAdminEntryPoint',
				action: 'failed',
				actionSubject: 'startAdminKeySession',
			},
		}).fire();

		flags.showErrorFlag({
			title: intl.formatMessage(i18n.enableSuperAdminErrorTitle),
			description: intl.formatMessage(i18n.enableSuperAdminErrorDescription),
		});
	};

	learnMoreClicked = () => {
		this.props
			.createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'adminKeyLearnMore',
					source: 'superAdminEntryPoint',
				},
			})
			.fire();

		/**
		 * Since EP is used for feature-based proactive billing experiment, generic events like link clicked
		 * cannot be used. Since in EP only action & actionSubject  can be used.
		 */
		this.props
			.createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'adminKeyLearnMoreLink',
					source: 'superAdminEntryPoint',
				},
			})
			.fire();
	};

	viewNowClicked = (enableSuperAdmin) => {
		this.props
			.createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'adminKeyViewNow',
					source: 'superAdminEntryPoint',
				},
			})
			.fire();
		enableSuperAdmin();
	};

	storeConfirmedPage = () => {
		const { contentId } = this.props;
		if (contentId) {
			localStorage.setItem(keys.RESTRICTED_PAGE_CONFIRMED, contentId);
		}
	};

	fireAnalyticsEvent = (articleId) => {
		const { createAnalyticsEvent } = this.props;

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'helpLink',
				source: 'superAdminEntryPoint',
				attributes: {
					targetArticleId: articleId,
					isSPA: true,
				},
			},
		}).fire();
	};

	renderActions = () => (
		<Subscribe to={[FlagsStateContainer]}>
			{(flags) => (
				<Mutation
					mutation={EnableSuperAdminMutation}
					onError={this.handleMutationComplete(flags)}
					onCompleted={this.handleMutationComplete(flags)}
				>
					{(enableSuperAdmin) => (
						<EntryPointActions>
							<AkButton appearance="primary" onClick={() => this.viewNowClicked(enableSuperAdmin)}>
								<FormattedMessage
									id="super-admin.start.session"
									description="Start super admin link"
									defaultMessage="View now"
								/>
							</AkButton>
							<Separator>&nbsp;</Separator>
							<InProductHelpButton
								articleId={LEARN_MORE_ARTICLE_ID}
								articleTopic="super-admin-entry-point"
								onClick={() => this.fireAnalyticsEvent(LEARN_MORE_ARTICLE_ID)}
							>
								<FormattedMessage
									id="super-admin.learn.more"
									description="Learn more about super admin link"
									defaultMessage="Learn more"
								/>
							</InProductHelpButton>
						</EntryPointActions>
					)}
				</Mutation>
			)}
		</Subscribe>
	);

	renderTitle = () => (
		<FormattedMessage
			id="super-admin.entry.point.title"
			description="Title for the super Super Admin entry point section"
			defaultMessage="View now with admin key"
			data-test-id="super-admin-entry-point-title"
		/>
	);

	renderMessage = () => (
		<FormattedMessage
			id="super-admin.entry.point.message"
			description="Message to the user about super admin troubleshooting session"
			defaultMessage="As a {canBeSuperAdmin} with Premium, you can use your admin key to view restricted content. We’ll log any restricted content you view and notify the owners."
			tagName="p"
			values={{
				canBeSuperAdmin: (
					<Link target="_blank" to="/admin/users?role=site%2Fadmin">
						<FormattedMessage
							id="super-admin.entry.point.message.can-be-super-admin"
							description="The human-readable user-facing description of a user who canBeSuperAdmin (i.e. Site admin)"
							defaultMessage="site admin"
						/>
					</Link>
				),
			}}
			data-test-id="super-admin-entry-point-message"
		/>
	);

	render = () => (
		<AdminKeyMessageCard
			title={this.renderTitle()}
			message={this.renderMessage()}
			actions={this.renderActions()}
			eventName="superAdminEntryPoint"
			type={this.props.type}
		/>
	);
}

export const SuperAdminEntryPoint = withAnalyticsEvents()(
	injectIntl(SuperAdminEntryPointComponent),
);
